import { SVGProps, useEffect, useRef, useState } from 'react';
import { createSvgImageUrl } from './svgUtils';
import { FALLBACK_IMAGE_ICON_INFO } from './const';

let FALLBACK_IMAGE_ICON_URL: string;
const getFallbackFallbackImageIconUrl = () => {
  if (!FALLBACK_IMAGE_ICON_URL) {
    FALLBACK_IMAGE_ICON_URL = createSvgImageUrl(FALLBACK_IMAGE_ICON_INFO, null);
  }
  return FALLBACK_IMAGE_ICON_URL;
};

type ImageHandler = (imageElement?: SVGImageElement) => void;
export type BrokenImageMouseEvents = {
  onBrokenImageMouseEnter?: ImageHandler;
  onBrokenImageMouseLeave?: ImageHandler;
};
export type CustomArdoqImageProps = BrokenImageMouseEvents & {
  customAttributes?: Record<string, string>;
  fallbackImageUri?: string;
};
type ArdoqSvgImageElementArgs = SVGProps<SVGImageElement> &
  CustomArdoqImageProps;
/**
 * Return an SVG image element, which replaces a broken image with fallbackImageUri if provided,
 * or with the default Ardoq broken image otherwise.
 * It calls the default DOMAttributes event handlers after the custom onBrokenImageMouseEnter/
 * onBrokenImageMouseLeave callbacks.
 */
export const ArdoqSvgImageElement = (args: ArdoqSvgImageElementArgs) => {
  const {
    href,
    customAttributes,
    fallbackImageUri,
    onBrokenImageMouseEnter,
    onBrokenImageMouseLeave,
    ...rest
  } = args;
  const [isBrokenImage, setIsBrokenImage] = useState(false);
  const [componentImageUri, setComponentImageUri] = useState(href);
  const imageElementRef = useRef<SVGImageElement | null>(null);

  useEffect(() => {
    if (isBrokenImage) {
      imageElementRef.current?.classList.add('broken-image');
    }
  }, [isBrokenImage]);

  return (
    <image
      {...rest}
      ref={imageElementRef}
      href={componentImageUri}
      // image element has global event attributes, onError is one of them
      // https://svgwg.org/svg2-draft/embedded.html#ImageElement
      // eslint-disable-next-line react/no-unknown-property
      onError={e => {
        setIsBrokenImage(true);
        setComponentImageUri(
          fallbackImageUri ?? getFallbackFallbackImageIconUrl()
        );
        rest.onError?.(e);
      }}
      onMouseEnter={e => {
        if (isBrokenImage) {
          onBrokenImageMouseEnter?.(imageElementRef.current || undefined);
        }
        rest.onMouseEnter?.(e);
      }}
      onMouseLeave={e => {
        if (isBrokenImage) {
          onBrokenImageMouseLeave?.(imageElementRef.current || undefined);
        }
        rest.onMouseLeave?.(e);
      }}
      {...customAttributes}
    />
  );
};
